import { useState, useEffect, useCallback } from 'react';
import {
  fetchClaimMdFiles, fetchClaimMdEras, openClaimMdEraPdf, fetchClaimMdEra835,
} from '../services/api';
import './Clearinghouse.css';

function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt)) return d;
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function fmtCurrency(n) {
  return '$' + (parseFloat(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const FILE_STATUS_LABELS = { accepted: 'Accepted', rejected: 'Rejected', partial: 'Partial' };

export default function Clearinghouse() {
  const [tab, setTab] = useState('transmissions'); // 'transmissions' | 'eras'

  // Transmissions (our 837 upload log)
  const [files, setFiles]               = useState([]);
  const [filesLoading, setFilesLoading] = useState(true);
  const [filesError, setFilesError]     = useState(null);

  // ERA inbox (Claim.MD's view)
  const [eras, setEras]                 = useState([]);
  const [erasLoading, setErasLoading]   = useState(false);
  const [erasError, setErasError]       = useState(null);
  const [erasLoaded, setErasLoaded]     = useState(false);
  const [busyEra, setBusyEra]           = useState(null); // `${eraid}:pdf|835`
  const [notice, setNotice]             = useState(null); // { type, message }

  const loadFiles = useCallback(async () => {
    setFilesLoading(true); setFilesError(null);
    try {
      const res = await fetchClaimMdFiles();
      setFiles(res.files || []);
    } catch (err) {
      setFilesError(err?.response?.data?.message || err?.response?.data?.error || 'Failed to load transmissions.');
    } finally {
      setFilesLoading(false);
    }
  }, []);

  const loadEras = useCallback(async () => {
    setErasLoading(true); setErasError(null);
    try {
      const res = await fetchClaimMdEras();
      setEras(res.era || []);
      setErasLoaded(true);
    } catch (err) {
      setErasError(err?.response?.data?.message || err?.response?.data?.error || 'Failed to load ERAs from Claim.MD.');
    } finally {
      setErasLoading(false);
    }
  }, []);

  useEffect(() => { loadFiles(); }, [loadFiles]);
  useEffect(() => { if (tab === 'eras' && !erasLoaded) loadEras(); }, [tab, erasLoaded, loadEras]);

  const handlePdf = async (eraid) => {
    setBusyEra(`${eraid}:pdf`);
    try { await openClaimMdEraPdf(eraid); }
    catch { setNotice({ type: 'error', message: `Could not load the remittance PDF for ERA ${eraid}.` }); }
    finally { setBusyEra(null); }
  };

  const handle835 = async (eraid) => {
    setBusyEra(`${eraid}:835`);
    try {
      const res = await fetchClaimMdEra835(eraid);
      const text = res?.era835 || '';
      const w = window.open('', '_blank', 'noopener');
      if (w) {
        w.document.title = `835 — ERA ${eraid}`;
        const pre = w.document.createElement('pre');
        pre.style.whiteSpace = 'pre-wrap';
        pre.style.fontFamily = 'monospace';
        pre.style.padding = '16px';
        pre.textContent = text;
        w.document.body.appendChild(pre);
      }
    } catch {
      setNotice({ type: 'error', message: `Could not load the 835 for ERA ${eraid}.` });
    } finally {
      setBusyEra(null);
    }
  };

  return (
    <div className="clr-page">
      <div className="clr-header">
        <div>
          <h1 className="clr-title">Clearinghouse</h1>
          <p className="clr-subtitle">
            {tab === 'transmissions'
              ? 'Claim batches transmitted to Claim.MD'
              : 'Remittances (ERAs) available from Claim.MD'}
          </p>
        </div>
        <button
          className="clr-btn-refresh"
          onClick={() => (tab === 'transmissions' ? loadFiles() : loadEras())}
          disabled={tab === 'transmissions' ? filesLoading : erasLoading}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
          Refresh
        </button>
      </div>

      <div className="clr-tabs">
        <button className={`clr-tab ${tab === 'transmissions' ? 'active' : ''}`} onClick={() => setTab('transmissions')}>
          Transmissions
          {files.length > 0 && <span className="clr-tab-count">{files.length}</span>}
        </button>
        <button className={`clr-tab ${tab === 'eras' ? 'active' : ''}`} onClick={() => setTab('eras')}>
          ERA Inbox
          {eras.length > 0 && <span className="clr-tab-count">{eras.length}</span>}
        </button>
      </div>

      {notice && (
        <div className={`clr-notice clr-notice--${notice.type}`}>
          <span>{notice.message}</span>
          <button className="clr-notice-close" onClick={() => setNotice(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      {/* ── Transmissions ─────────────────────────────────────────────── */}
      {tab === 'transmissions' && (
        filesLoading ? (
          <div className="clr-state"><div className="clr-spinner" /><span>Loading transmissions…</span></div>
        ) : filesError ? (
          <div className="clr-error">{filesError}<button onClick={loadFiles}>Retry</button></div>
        ) : files.length === 0 ? (
          <div className="clr-empty">
            <p>No claim batches transmitted yet.</p>
            <p className="clr-empty-sub">Submitting generated claims to Claim.MD from the Worklist will list them here.</p>
          </div>
        ) : (
          <div className="clr-table-wrap">
            <table className="clr-table">
              <thead>
                <tr>
                  <th>File</th><th>Form</th><th>Claims</th><th>Accepted</th><th>Rejected</th><th>Status</th><th>Submitted</th>
                </tr>
              </thead>
              <tbody>
                {files.map((f) => (
                  <tr key={f.claimmd_file_id}>
                    <td className="clr-td-mono">{f.filename}</td>
                    <td>{f.form_type}</td>
                    <td className="clr-td-center">{f.claim_count}</td>
                    <td className="clr-td-center clr-accepted">{f.accepted_count}</td>
                    <td className="clr-td-center clr-rejected">{f.rejected_count}</td>
                    <td><span className={`clr-badge clr-badge-${f.status}`}>{FILE_STATUS_LABELS[f.status] || f.status}</span></td>
                    <td>{fmtDate(f.submitted_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* ── ERA Inbox ─────────────────────────────────────────────────── */}
      {tab === 'eras' && (
        <>
          <div className="clr-note">
            This lists every ERA Claim.MD has for the account. To post them into remit batches,
            use <strong>Payments → Sync Remits</strong>.
          </div>
          {erasLoading ? (
            <div className="clr-state"><div className="clr-spinner" /><span>Loading ERAs from Claim.MD…</span></div>
          ) : erasError ? (
            <div className="clr-error">{erasError}<button onClick={loadEras}>Retry</button></div>
          ) : eras.length === 0 ? (
            <div className="clr-empty"><p>No ERAs available from Claim.MD right now.</p></div>
          ) : (
            <div className="clr-table-wrap">
              <table className="clr-table">
                <thead>
                  <tr>
                    <th>ERA ID</th><th>Payer</th><th>Check / EFT #</th><th className="num">Amount</th><th>Paid Date</th><th>Provider</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {eras.map((e) => (
                    <tr key={e.eraid}>
                      <td className="clr-td-mono">{e.eraid}</td>
                      <td>{e.payer_name || '—'}</td>
                      <td className="clr-td-mono">{e.check_number || '—'}</td>
                      <td className="clr-td-amount">{fmtCurrency(e.paid_amount)}</td>
                      <td>{fmtDate(e.paid_date)}</td>
                      <td>{e.prov_name || '—'}</td>
                      <td className="clr-td-actions">
                        <button className="clr-action" onClick={() => handlePdf(e.eraid)} disabled={busyEra === `${e.eraid}:pdf`}>
                          {busyEra === `${e.eraid}:pdf` ? '…' : 'PDF'}
                        </button>
                        <button className="clr-action" onClick={() => handle835(e.eraid)} disabled={busyEra === `${e.eraid}:835`}>
                          {busyEra === `${e.eraid}:835` ? '…' : '835'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
